/**
 * Lightweight, provider-agnostic client-side error/telemetry hook.
 *
 * Every error/event in the app should flow through `reportError` /
 * `reportEvent` below rather than calling `console.*` directly, so there is
 * exactly one choke point to swap when a real telemetry backend is wired up
 * (see docs/production-readiness/WEB_CLIENT_ALIGNMENT_AND_PRODUCTION_READINESS_PLAN.md
 * §7.4). Today the "sink" is just a structured console call; the shape of
 * the payload (timestamp, message, stack, context/data, href) is deliberately
 * the same shape a real SDK call would need, so swapping the sink later is a
 * one-line change per function, not a rewrite of every call site.
 *
 * No live Application Insights / OpenTelemetry connection is configured in
 * this environment - do NOT import an Azure/OTel SDK here until one is.
 */

type JsonRecord = Record<string, unknown>;

interface BaseTelemetryPayload {
  timestamp: string;
  message: string;
  href: string;
}

interface ErrorTelemetryPayload extends BaseTelemetryPayload {
  stack?: string;
  context?: JsonRecord;
}

interface EventTelemetryPayload extends BaseTelemetryPayload {
  data?: JsonRecord;
}

// Errors we've already reported, so the same Error instance caught by both a
// React ErrorBoundary and a global window listener (or reported twice by
// nested boundaries) is only ever sent once. WeakSet holds no strong
// reference to the error, so this can never leak memory.
const reportedErrors = new WeakSet<object>();

function isReported(error: unknown): boolean {
  return typeof error === "object" && error !== null && reportedErrors.has(error);
}

function markReported(error: unknown): void {
  if (typeof error === "object" && error !== null) {
    reportedErrors.add(error);
  }
}

function currentHref(): string {
  return typeof window !== "undefined" && window.location ? window.location.href : "";
}

function describeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  if (typeof error === "string") {
    return { message: error };
  }
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

function safeStringify(payload: unknown): unknown {
  try {
    return JSON.stringify(payload);
  } catch {
    // Circular or non-serializable context/data - fall back to the raw
    // object so console still shows something useful.
    return payload;
  }
}

/**
 * Reports an unexpected error/exception. Safe to call from anywhere: a React
 * ErrorBoundary's componentDidCatch, the global `window.onerror` /
 * `unhandledrejection` listeners, or an ad-hoc try/catch in feature code.
 *
 * De-dupes on the Error object instance, so the same error surfacing through
 * multiple sinks (e.g. an ErrorBoundary and then a global listener) is only
 * reported once. The whole function is wrapped so a bug here can never
 * itself throw an uncaught error/loop back into the global handlers.
 */
export function reportError(error: unknown, context?: JsonRecord): void {
  try {
    if (isReported(error)) {
      return;
    }
    markReported(error);

    const { message, stack } = describeError(error);
    const payload: ErrorTelemetryPayload = {
      timestamp: new Date().toISOString(),
      message,
      href: currentHref(),
      ...(stack ? { stack } : {}),
      ...(context ? { context } : {}),
    };

    // ---- SINK: replace this block with the real telemetry call later ----
    // e.g. Application Insights:
    //   appInsights.trackException({ exception: error instanceof Error ? error : new Error(message), properties: { ...context, href: payload.href } });
    // or OpenTelemetry:
    //   tracer.startSpan("client-error", { attributes: { ...context } }).recordException(error as Error).end();
    // Everything above (payload shaping/de-dupe) can stay exactly as-is.
    console.error("[telemetry:error]", safeStringify(payload));
  } catch {
    // Telemetry must never be the source of a new uncaught error - swallow.
  }
}

/**
 * Reports a non-error telemetry event (feature usage, milestones, etc.).
 */
export function reportEvent(name: string, data?: JsonRecord): void {
  try {
    const payload: EventTelemetryPayload = {
      timestamp: new Date().toISOString(),
      message: name,
      href: currentHref(),
      ...(data ? { data } : {}),
    };

    // ---- SINK: replace this block with the real telemetry call later ----
    // e.g. Application Insights:
    //   appInsights.trackEvent({ name }, { ...data, href: payload.href });
    // or OpenTelemetry:
    //   tracer.startSpan(name, { attributes: { ...data } }).end();
    console.info("[telemetry:event]", safeStringify(payload));
  } catch {
    // Telemetry must never be the source of a new uncaught error - swallow.
  }
}

let globalHandlersInstalled = false;

/**
 * Registers window-level listeners so uncaught errors and unhandled promise
 * rejections are captured even when they happen outside any React
 * ErrorBoundary (event handlers, timers, fire-and-forget async code, etc).
 * Idempotent - safe to call more than once, only wires listeners once.
 */
export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled || typeof window === "undefined") {
    return;
  }
  globalHandlersInstalled = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    reportError(event.error ?? event.message, {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportError(event.reason, { source: "unhandledrejection" });
  });
}
